import { Injectable } from '@angular/core';
import { Contact } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { AudioType } from '@app/preferences/models/audio-type.enum';
import {
  TTSLanguage,
  TTSLanguagesDto,
  TTSVoice,
  VoicemailFileGreeting,
  VoicemailGreetingAudioItem,
  VoicemailSettingsModel,
  VoicemailTTSGreeting,
} from '@app/preferences/models/voicemail.model';
import { SettingsService } from '@app/preferences/services/settings.service';
import { AudioUtil } from '@app/shared/utils/audio.util';
import { saveAs } from 'file-saver-es';
import { firstValueFrom, Observable } from 'rxjs';

import { VoicemailSettingsApiService } from './voicemail-settings-api.service';
import { VoicemailSettingsStateService } from './voicemail-settings-state.service';

@Injectable()
export class VoicemailSettingsFacadeService {
  constructor(
    private api: VoicemailSettingsApiService,
    private state: VoicemailSettingsStateService,
    private settingsService: SettingsService,
    private contactService: ContactService
  ) {}

  public getVoicemailSettings(): void {
    this.getCurrentUser();
    this.api.getVoicemailSettings().subscribe((response: VoicemailSettingsModel) => {
      this.state.updateVoiceMailSettings(response);
      this.getTTSLanguages();
      this.getGreetingsList();
      this.loadRecordedName();
    });
  }

  public getVoicemailSettings$(): Observable<VoicemailSettingsModel | undefined> {
    return this.state.getVoiceMailSettings$();
  }

  public getGreetingList$(): Observable<VoicemailGreetingAudioItem[]> {
    return this.state.getGreetingsList$();
  }

  public deleteGreetingAudioItem(greetingAudioItem: VoicemailGreetingAudioItem) {
    this.api.deleteAudioFile(AudioType.greeting, greetingAudioItem.index).subscribe(() => {
      this.state.deleteGreetingAudioItem(greetingAudioItem);
    });
  }

  public deleteRecordedNameAudioItem() {
    this.api.deleteAudioFile(AudioType.name, '1').subscribe(() => {
      this.state.setRecordedName();
    });
  }

  public async saveVoicemailSettings(settings: VoicemailSettingsModel): Promise<void> {
    await firstValueFrom(this.api.saveVoicemailSettings(settings));
    this.state.updateVoiceMailSettings(settings);
    this.updateVoicemailSettingsSaved(true);
  }

  public getVoicemailSettingsSaved$(): Observable<boolean> {
    return this.state.getVoicemailSettingsSaved$();
  }

  public updateVoicemailSettingsSaved(successful: boolean): void {
    this.state.updateVoicemailSettingsSaved(successful);
  }

  public getVoicemailPinUpdated$(): Observable<boolean> {
    return this.state.getVoicemailPinUpdated$();
  }

  public async updatePin(pin: string): Promise<void> {
    await firstValueFrom(this.api.updatePin(pin));
    this.state.setVoicemailPinUpdated();
  }

  public getTTSLanguages(): void {
    this.api.getTTSLanguages().subscribe((response: TTSLanguagesDto) => {
      this.state.setTTSLanguages$(response.languages);
    });
  }

  public getTTSLanguages$(): Observable<TTSLanguage[]> {
    return this.state.getTTSLanguages$();
  }

  public getTTSVoices$(): Observable<TTSVoice[]> {
    return this.state.getTTSVoices$();
  }

  public getTTSVoiceOfLanguage(languageId: string): void {
    if (this.state.isVoiceOfLanguageLoaded(languageId)) {
      this.state.setTTSVoices(languageId);
    } else {
      this.api.getTTSVoices(languageId).subscribe((voices: TTSVoice[]) => {
        this.state.setTTSVoicesOfLanguage({ language: languageId, voices });
        this.state.setTTSVoices(languageId);
      });
    }
  }

  private getCurrentUser() {
    this.contactService.currentUser$.subscribe((contact) => this.state.setCurrentUser(contact));
  }

  public getCurrentUser$(): Observable<Contact | undefined> {
    return this.state.getCurrentUser$();
  }

  public getAudioUploaded$(): Observable<boolean> {
    return this.state.getAudioUploaded$();
  }

  public getRecordedName$(): Observable<VoicemailGreetingAudioItem | undefined> {
    return this.state.getRecordedName$();
  }

  public setRecordedName(recordedName: VoicemailGreetingAudioItem | undefined) {
    this.state.setRecordedName(recordedName);
  }

  public loadRecordedName(): void {
    this.api.getListOfRecordedNames().subscribe((recordedNames) => {
      this.setRecordedName(recordedNames.length > 0 ? recordedNames[0] : undefined);
    });
  }

  public async uploadTTS(
    audio: VoicemailTTSGreeting,
    index: number | string,
    audioType: AudioType
  ): Promise<void | { url: string }> {
    await firstValueFrom(this.api.uploadTTS(audio, index, audioType));
    this.state.setAudioUploaded();
  }

  private async readFileAsBlob(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const blob = new Blob([reader.result!], { type: file.type });
        resolve(blob);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  public async uploadAudioFile(greeting: VoicemailFileGreeting, audioType: AudioType): Promise<void> {
    const format = greeting.file?.name.split('.').pop() ?? greeting.name.split('.').pop() ?? 'wav';
    const res = await firstValueFrom(
      this.api.uploadAudio(greeting.name, greeting.description, greeting.index, audioType, format)
    );

    const blob = greeting.file
      ? await this.readFileAsBlob(greeting.file)
      : await firstValueFrom(this.api.downloadAudioFile(greeting.index.toString(), audioType));

    await firstValueFrom(this.api.uploadFileGreeting(blob, res.url));
    this.state.setAudioUploaded();
  }

  public downloadAudioFile(index: string, type: AudioType, description: string) {
    this.api.downloadAudioFile(index, type).subscribe((response) => {
      saveAs(response, `${type}-${description}.wav`);
    });
  }

  public getGreetingsList() {
    this.api.getListOfGreetings().subscribe((res) => {
      this.state.updateGreetingsList(res);
    });
  }

  public previewTts(voice: TTSVoice, text: string) {
    this.api.previewTTS(voice, text).subscribe((response) => {
      const audioUtil: AudioUtil = new Audio(URL.createObjectURL(new Blob([response]))) as AudioUtil;
      void this.playAudio(audioUtil);
    });
  }

  public getPlayingAudio$(): Observable<AudioUtil | undefined> {
    return this.state.getPlayingAudio$();
  }

  public async playAudio(audio: AudioUtil) {
    audio.addEventListener('play', () => {
      this.state.setPlayingAudio(audio);
    });
    audio.addEventListener('ended', () => {
      this.state.setPlayingAudio();
    });
    await this.settingsService.playAudio(audio);
  }

  public stopAudio() {
    this.state.setPlayingAudio();
  }
}
